(() => {
  'use strict';
  const duration = 240;
  const easing = 'cubic-bezier(.2,.7,.2,1)';
  const records = new WeakMap();
  const prepared = new WeakSet();
  const running = new Set();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 989px)');
  const enabled = () => document.body.classList.contains('animations-enabled') && !reduced.matches;
  function prepare(element) {
    if (prepared.has(element)) return;
    prepared.add(element);
    element.dataset.oleaOverlayMotion = 'true';
    if (element.matches('.mega-panel')) element.querySelector(':scope > .mega-panel__inner')?.setAttribute('data-olea-overlay-motion', 'true');
    if (element instanceof HTMLDialogElement) {
      element.addEventListener('cancel', event => {
        if (event.defaultPrevented) return;
        event.preventDefault();
        hide(element);
      });
      element.addEventListener('close', () => {
        if (!element.open && !records.get(element)?.finishing) cancel(element);
      });
    }
  }
  function current(element) {
    const style = getComputedStyle(element);
    return { translate: style.translate, opacity: style.opacity };
  }
  function offset(element, rest) {
    const values = rest.translate === 'none' ? ['0px', '0px'] : rest.translate.split(/\s+/);
    const distance = mobile.matches ? `${Math.max(0, innerHeight - element.getBoundingClientRect().top + 1)}px` : '12px';
    return `${values[0]} calc(${values[1] || '0px'} + ${distance})${values[2] ? ' ' + values[2] : ''}`;
  }
  function cancel(element) {
    const record = records.get(element);
    if (!record) return null;
    const snapshot = current(element);
    snapshot.backdrop = element.matches(':modal') ? getComputedStyle(element, '::backdrop').opacity : '1';
    records.delete(element);
    running.delete(element);
    record.animations.forEach(animation => animation.cancel());
    record.resolve(false);
    return snapshot;
  }
  function play(element, phase, action, { immediate = false } = {}) {
    if (!element) { action?.(); return Promise.resolve(false); }
    prepare(element);
    const existing = records.get(element);
    if (phase === 'close' && existing?.phase === 'close') {
      if (immediate) existing.finish();
      return existing.promise;
    }
    const visible = element.getClientRects().length > 0 && !element.hidden && (!(element instanceof HTMLDialogElement) || element.open);
    const snapshot = cancel(element);
    if (phase === 'open' && action?.() === false) return Promise.resolve(false);
    const complete = () => phase === 'close' ? action?.() : undefined;
    const animate = !(immediate || !enabled() || typeof element.animate !== 'function' || !element.isConnected || phase === 'close' && !visible || phase === 'open' && visible && !snapshot);
    const animations = [];
    if (animate) {
      const rest = current(element);
      const away = { translate: element.matches('[data-mega-close]') ? rest.translate : offset(element, rest), opacity: '0' };
      const from = snapshot || (phase === 'open' ? away : rest);
      const to = phase === 'open' ? rest : away;
      animations.push(element.animate([{ translate: from.translate, opacity: from.opacity }, to], { duration, easing, fill: 'both' }));
      if (element.matches(':modal')) {
        try {
          const backdrop = getComputedStyle(element, '::backdrop').opacity;
          animations.push(element.animate([{ opacity: snapshot?.backdrop || (phase === 'open' ? '0' : backdrop) }, { opacity: phase === 'open' ? backdrop : '0' }], { duration, easing, fill: 'both', pseudoElement: '::backdrop' }));
        } catch {}
      }
    }
    let resolve;
    const promise = new Promise(done => { resolve = done; });
    const record = { phase, animations, promise, resolve, finish: null, finishing: false };
    record.finish = () => {
      if (records.get(element) !== record || record.finishing) return;
      record.finishing = true;
      Promise.resolve(complete()).then(() => {
        if (records.get(element) === record) {
          records.delete(element);
          running.delete(element);
        }
        animations.forEach(animation => animation.cancel());
        resolve(true);
      });
    };
    records.set(element, record);
    running.add(element);
    if (animate) Promise.all(animations.map(animation => animation.finished)).then(record.finish, () => {});
    else record.finish();
    return promise;
  }
  function show(dialog, { modal = true, immediate = false } = {}) {
    if (!dialog) return Promise.resolve(false);
    return play(dialog, 'open', () => {
      dialog.classList.remove('is-opening', 'is-closing');
      dialog.classList.add('is-open');
      if (!dialog.open) modal ? dialog.showModal() : dialog.show();
    }, { immediate });
  }
  function hide(dialog, { immediate = false, returnValue, after } = {}) {
    if (!dialog) { after?.(); return Promise.resolve(false); }
    return play(dialog, 'close', () => {
      const finish = () => {
        if (!dialog.open) {
          dialog.classList.remove('is-opening', 'is-open', 'is-closing');
          after?.();
        }
      };
      if (!dialog.open) { finish(); return; }
      return new Promise(resolve => {
        dialog.addEventListener('close', () => { finish(); resolve(); }, { once: true });
        returnValue === undefined ? dialog.close() : dialog.close(returnValue);
      });
    }, { immediate });
  }
  window.OleaOverlayMotion = {
    duration, easing, enabled, prepare, show, hide, cancel,
    open: (element, reveal, options) => play(element, 'open', reveal, options),
    close: (element, conceal, options) => play(element, 'close', conceal, options),
    finished: element => records.get(element)?.promise || Promise.resolve(true),
    isClosing: element => records.get(element)?.phase === 'close'
  };
  reduced.addEventListener('change', () => { if (reduced.matches) [...running].forEach(element => records.get(element)?.finish()); });
  window.addEventListener('pagehide', () => [...running].forEach(element => records.get(element)?.finish()));
  window.addEventListener('submit', event => {
    if (event.defaultPrevented || !(event.target instanceof HTMLFormElement)) return;
    const form = event.target;
    const method = event.submitter?.getAttribute('formmethod') || form.getAttribute('method');
    if (method?.toLowerCase() !== 'dialog') return;
    const dialog = form.closest('dialog');
    if (!dialog) return;
    event.preventDefault();
    hide(dialog, { returnValue: event.submitter?.value || '' });
  });
})();
